<template>
  <!--最小值和最大值数据源 -->
  <template v-for="item in inputItems" :key="item.key">
    <AttrPrecisionTime
      v-if="item.isTimePicker"
      :label="item.label"
      :width="128"
      :value="item.value"
      :disabled="!mainSourceItem || disabled"
      :readonly="item.isSourceKey"
      :underline="item.isSourceKey"
      @change="(val:number)=>onNumberChange(item.key,val)"
    >
      <icon
        v-if="enableBindSource || item.isSourceKey"
        class="ml-1 cursor-pointer opacity-70"
        :class="{
          ' attr-disabled': !mainSourceItem || disabled
        }"
        :icon="item.isSourceKey ? 'delete2.svg' : 'add.svg'"
        :width="14"
        :height="14"
        fill="none"
        @click="
          () => {
            if (!mainSourceItem || disabled) return;
            addOrDelSource(item, !item.isSourceKey);
          }
        "
      />
    </AttrPrecisionTime>
    <AttrInputNumber
      v-else
      :label="item.label"
      :width="item.isSourceKey ? 160 : 60"
      :value="item.value"
      :disabled="!mainSourceItem || disabled"
      :readonly="item.isSourceKey"
      :underline="item.isSourceKey"
      :min="mainSourceItem?.minValue"
      :max="mainSourceItem?.maxValue"
      :precision="mainSourceItem?.precision"
      :step="
        mainSourceItem?.precision ? Math.pow(0.1, mainSourceItem.precision) : 1
      "
      pointer-on-readonly
      @change="(val:number)=>onNumberChange(item.key,val)"
      @readonly-click="() => addOrDelSource(item, true)"
    >
      <icon
        v-if="enableBindSource || item.isSourceKey"
        class="ml-1 cursor-pointer opacity-70"
        :class="{
          'attr-disabled': !mainSourceItem || disabled
        }"
        :icon="item.isSourceKey ? 'delete2.svg' : 'add.svg'"
        :width="14"
        :height="14"
        fill="none"
        @click="
          () => {
            if (!mainSourceItem || disabled) return;
            addOrDelSource(item, !item.isSourceKey);
          }
        "
      />
    </AttrInputNumber>
  </template>
  <modal-data-source
    ref="sourceModal"
    disable-string
    :source-key="currentInputSourceItem?.key"
    :source-tags="
      props.shape.isMultiUnitSource
        ? ['normal', 'compound']
        : ['normal', 'subunit_1']
    "
    hidden-fake-item
    @select="selectDataSource"
  />
</template>

<script lang="ts" setup>
import { useDataSource } from '@/hooks';
import { Shape, ShapeType } from '@/services/shape';
import { ImageAttribute } from '@/services/shape/attributes/image';
import { canUse } from '@/utils/can-use.utils';
import { isSourceKeyFormat } from '@/utils/number';
import { PropType, computed, nextTick, ref } from 'vue';

const { findOne } = useDataSource();

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const enableBindSource = computed(() => {
  return (
    [ShapeType.Image, ShapeType.ProgressBar].includes(props.shape.type) &&
    canUse('data-source-bind-max-min-value-support').valid
  );
});
const inputItems = computed(() => {
  const { attrs, sourceItem } = props.shape;
  const { valueStart, valueEnd } = attrs;
  // const isTimePicker = [
  //   'weatherCurrentSunRise',
  //   'weatherCurrentSunSet',
  //   'appAlarm'
  // ].includes(source);
  const isTimePicker = false;

  const units = sourceItem?.unit?.split('/') ?? [];
  const unit = units[1] ? `（${units[0]}）` : '';
  const res: {
    key: 'valueStart' | 'valueEnd';
    label: string;
    isSourceKey?: boolean;
    value: string | number | undefined;
    isTimePicker: boolean;
  }[] = [
    {
      key: 'valueStart',
      label: '最小数值' + unit,
      isSourceKey: isSourceKeyFormat(valueStart),
      value: valueStart,
      isTimePicker: isTimePicker
    },
    {
      key: 'valueEnd',
      label: '最大数值' + unit,
      isSourceKey: isSourceKeyFormat(valueEnd),
      value: valueEnd,
      isTimePicker: isTimePicker
    }
  ];
  res.forEach((item) => {
    if (item.isSourceKey) {
      item.value = findOne(item.value + '')?.label;
    }
  });

  return res;
});

const sourceModal = ref();

const mainSourceItem = computed(() => props.shape.sourceItem);

//初始化数据源
const currentInputKey = ref<(typeof inputItems.value)[0]['key']>();
const currentInputSourceItem = ref<DataSourceItem>();

const onNumberChange = (
  key: (typeof inputItems.value)[0]['key'],
  val: number
) => {
  _setAttrs({
    [key]: val
  });
};

const valid = (
  key1: (typeof inputItems.value)[0]['key'],
  val: string | number
) => {
  const isSourceKey = isSourceKeyFormat(val);
  const inputItem = inputItems.value.find((item) => item.key !== key1);
  const isSourceKey2 = isSourceKeyFormat(inputItem?.value);
  if (!inputItem) return;

  if (key1 == 'valueStart') {
    if (isSourceKey) {
      // 开始值设置为数据源时，end也必须为数据源
      if (!isSourceKey2) {
        _setAttrs({
          [inputItem.key]: val
        });
      }
    } else {
      // do nothing
    }
  } else {
    if (isSourceKey) {
      //  do nothing
    } else {
      // 结束值设置了数字，则起始值也必须为数字
      if (isSourceKey2) {
        _setAttrs({
          [inputItem.key]: mainSourceItem?.value?.minValue ?? 0
        });
      }
    }
  }
};

const selectDataSource = (item: DataSourceItem) => {
  const key = currentInputKey.value ?? 'valueStart';
  _setAttrs({
    [key]: item.key
  });
  valid(key, item.key);
};

const addOrDelSource = async (
  item: (typeof inputItems.value)[0],
  status: boolean
) => {
  const key = item.key;
  if (status) {
    // open source modal
    currentInputKey.value = key;
    currentInputSourceItem.value = item.isSourceKey
      ? findOne(item.value + '')
      : undefined;
    await nextTick();
    sourceModal.value?.open?.();
  } else {
    // remove
    const sourceItem = mainSourceItem?.value;
    const val =
      key == 'valueStart'
        ? sourceItem?.minValue ?? 0
        : sourceItem?.maxValue ?? 100;
    _setAttrs({
      [key]: val
    });
    valid(key, val);
  }
};

const _setAttrs = (data: Partial<ImageAttribute>) => {
  props.shape.setAttrs(data);
};
</script>
<style scoped></style>
